import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaPaperPlane, FaTrash } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';

// The call page's Generative AI Query as a right-hand panel. From xl (1280px) up it is a sticky
// column beside the page that folds down to a slim rail; below xl there is no room for a column,
// so it is a drawer opened from a floating "Ask AI" button.

export const PANEL_OPEN_KEY = 'callPage.genaiPanelOpen';
const WIDE_QUERY = '(min-width: 1280px)';
const SUGGESTIONS = [
  'Summarise this call.',
  'What objections did the customer raise?',
  'Did the agent miss anything required?',
];
const PENDING = '...';

const isWide = () => Boolean(window.matchMedia?.(WIDE_QUERY)?.matches);

// Open/collapsed state for the panel, kept in Dashboard because it also sets the page's grid
// columns. Only the desktop choice is remembered: closing the drawer on a phone must not
// collapse the panel for the next desktop visit, and the drawer always starts closed.
export const useGenAIPanelOpen = () => {
  const [open, setOpenState] = useState(() => {
    if (!isWide()) return false;
    try {
      return window.localStorage.getItem(PANEL_OPEN_KEY) !== '0';
    } catch (_) {
      return true;
    }
  });

  const setOpen = useCallback((next) => {
    setOpenState(next);
    if (!isWide()) return;
    try {
      window.localStorage.setItem(PANEL_OPEN_KEY, next ? '1' : '0');
    } catch (_) {
      // Storage blocked (private window, site data cleared): the choice just isn't remembered.
    }
  }, []);

  return [open, setOpen];
};

// Your questions on the right in a tinted bubble, the AI's answers on the left - the same chat
// layout as the transcript.
const GenAIBubble = ({ from, text, isSelf, pending }) => (
  <div className={`flex ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
    <div className={`min-w-0 max-w-[88%] flex flex-col ${isSelf ? 'items-end' : 'items-start'}`}>
      <span className={`text-xs font-semibold mb-1 ${isSelf ? 'text-cloudza-700' : 'text-gray-700'}`}>{from}</span>
      <div
        className={`px-3.5 py-2.5 text-sm leading-relaxed ${
          isSelf
            ? 'bg-cloudza-50 text-gray-800 rounded-2xl rounded-tr-sm'
            : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-tl-sm'
        }`}
      >
        {pending ? (
          <span className="inline-flex items-center gap-1" aria-label="Thinking">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" />
          </span>
        ) : (
          <span className="whitespace-pre-line">{text}</span>
        )}
      </div>
    </div>
  </div>
);

const SparkleBadge = ({ large = false }) => (
  <span
    className={`flex flex-shrink-0 items-center justify-center text-white bg-gradient-to-br from-cloudza-500 to-cloudza-700 ${
      large ? 'h-11 w-11 rounded-2xl' : 'h-8 w-8 rounded-lg'
    }`}
    aria-hidden="true"
  >
    <HiSparkles className={large ? 'h-5 w-5' : 'h-4 w-4'} />
  </span>
);

const UnreadBadge = ({ count, inverted = false }) =>
  count > 0 ? (
    <span
      className={`min-w-[18px] h-[18px] px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${
        inverted ? 'bg-white text-cloudza-700' : 'bg-cloudza-600 text-white'
      }`}
      aria-hidden="true"
    >
      {count}
    </span>
  ) : null;

// Answers that went from pending to text between two renders of the history.
const newlyAnswered = (before, after) =>
  after.filter((entry, i) => before[i]?.answer === PENDING && entry.answer !== PENDING).length;

const GenAIPanel = ({ history = [], onSubmit, onClear, open, onOpenChange }) => {
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(0);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const previousHistory = useRef(history);

  useEffect(() => {
    const arrived = newlyAnswered(previousHistory.current, history);
    previousHistory.current = history;
    if (arrived > 0 && !open) setUnread((n) => n + arrived);
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [history, open]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // Escape closes the drawer on narrow screens; on desktop the column stays as it is.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      // defaultPrevented: a modal on top already used this Escape.
      if (e.key === 'Escape' && !e.defaultPrevented && !isWide()) onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  const resizeInput = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    onSubmit(text);
    setDraft('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const openPanel = () => {
    onOpenChange(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const openLabel = `Open Generative AI Query${unread > 0 ? ` (${unread} new)` : ''}`;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[65] bg-gray-900/25 xl:hidden"
          aria-hidden="true"
          onClick={() => onOpenChange(false)}
        />
      )}

      <aside
        id="genai-panel"
        aria-label="Generative AI Query"
        className={`flex flex-col overflow-hidden bg-white border border-gray-200 rounded-[14px] shadow-[0_1px_2px_rgba(23,21,31,0.04),0_8px_24px_-12px_rgba(76,29,149,0.18)]
          fixed top-3 bottom-3 right-3 z-[70] w-[min(400px,calc(100vw-24px))] transition-[transform,visibility] duration-200
          ${open ? 'translate-x-0 visible' : 'translate-x-[calc(100%+24px)] invisible'}
          xl:sticky xl:top-4 xl:bottom-auto xl:right-auto xl:z-auto xl:w-auto xl:translate-x-0 xl:visible
          xl:h-[calc(100vh-3.5rem-2.5rem)] xl:min-h-[360px]`}
      >
        {!open && (
          <button
            type="button"
            onClick={openPanel}
            aria-controls="genai-panel"
            aria-expanded="false"
            aria-label={openLabel}
            title="Open Generative AI Query"
            className="hidden xl:flex h-full w-full flex-col items-center gap-3.5 py-2.5 text-gray-600 hover:text-cloudza-700 hover:bg-cloudza-50/40 transition-colors"
          >
            <SparkleBadge />
            <UnreadBadge count={unread} />
            <span className="text-xs font-semibold tracking-wide [writing-mode:vertical-rl]">Generative AI Query</span>
            <FaChevronLeft className="mt-auto h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}

        <div className={`flex min-h-0 flex-1 flex-col ${open ? '' : 'xl:hidden'}`}>
          <div className="flex items-center gap-2.5 border-b border-gray-100 py-3.5 pl-4 pr-3">
            <SparkleBadge />
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-gray-900 leading-tight">Generative AI Query</h2>
              <p className="text-xs text-gray-400 leading-tight">Ask anything about this call</p>
            </div>
            {history.length > 0 && onClear && (
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear conversation"
                title="Clear conversation"
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <FaTrash className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-controls="genai-panel"
              aria-expanded={open}
              aria-label="Collapse Generative AI Query"
              title="Collapse panel"
              className={`${history.length > 0 && onClear ? '' : 'ml-auto '}flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-cloudza-700 transition-colors`}
            >
              <FaChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

          <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4" aria-live="polite">
            {history.length === 0 ? (
              <div className="my-auto px-1 py-2 text-center">
                <div className="mb-2.5 flex justify-center"><SparkleBadge large /></div>
                <h3 className="text-sm font-semibold text-gray-800">Ask a question about this call</h3>
                <p className="mt-1 mb-3.5 text-sm text-gray-400">Answers come from this call’s transcript.</p>
                <div className="grid gap-1.5 text-left">
                  {SUGGESTIONS.map((text) => (
                    <button
                      key={text}
                      type="button"
                      onClick={() => onSubmit(text)}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-600 hover:border-cloudza-500 hover:bg-cloudza-50 hover:text-cloudza-700 transition-colors"
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              history.map((entry, idx) => (
                <React.Fragment key={idx}>
                  <GenAIBubble from="You" text={entry.question} isSelf />
                  <GenAIBubble from="AI" text={entry.answer} pending={entry.answer === PENDING} />
                </React.Fragment>
              ))
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50 px-3.5 py-3">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-end gap-2 rounded-xl border border-gray-300 bg-white py-1.5 pl-3 pr-1.5 focus-within:border-cloudza-500 focus-within:ring-2 focus-within:ring-cloudza-100"
            >
              <textarea
                ref={inputRef}
                rows={1}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); resizeInput(e.target); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask a question about this call..."
                aria-label="Question"
                className="max-h-[120px] flex-1 resize-none border-0 bg-transparent py-1.5 text-sm leading-snug outline-none focus:ring-0"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={!draft.trim()}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cloudza-600 text-white hover:bg-cloudza-700 disabled:cursor-default disabled:bg-gray-300 transition-colors"
              >
                <FaPaperPlane className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </form>
            <p className="mt-1.5 text-[11px] text-gray-400">Enter to send · Shift+Enter for a new line</p>
          </div>
        </div>
      </aside>

      {!open && (
        <button
          type="button"
          onClick={openPanel}
          aria-controls="genai-panel"
          aria-expanded="false"
          className="genai-launcher fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-cloudza-600 px-[18px] py-[11px] text-sm font-semibold text-white shadow-[0_10px_24px_-8px_rgba(76,29,149,0.55)] hover:bg-cloudza-700 transition-colors xl:hidden"
        >
          <HiSparkles className="h-4 w-4" aria-hidden="true" />
          Ask AI
          <UnreadBadge count={unread} inverted />
        </button>
      )}
    </>
  );
};

export default GenAIPanel;
